import { de } from "../../../website/src/data/de";
import { en } from "../../../website/src/data/en";
import {
  additionalPrices,
  additionalPricesEn,
  galleryImages,
  pitchPrices,
  pitchPricesEn,
  site,
} from "../../../website/src/data/site";
import { siteImages } from "../../../website/src/data/siteImages";
import type { FacilityTab } from "../../../website/src/data/types";

export type WebsiteTextField = {
  key: string;
  label: string;
  group: string;
  multiline: boolean;
  de: string;
  en: string;
};

export type WebsiteSharedField = {
  key: string;
  label: string;
  group: string;
  value: string;
};

export type WebsiteImageField = {
  key: string;
  label: string;
  group: string;
  src: string;
  altDe: string;
  altEn: string;
};

function textFields(): WebsiteTextField[] {
  const fields: WebsiteTextField[] = [];
  const add = (
    key: string,
    label: string,
    group: string,
    german: string,
    english: string,
    multiline = false,
  ) => {
    fields.push({ key, label, group, multiline, de: german, en: english });
  };

  add("hero.welcome", "Zeile über der Überschrift", "Startseite", de.hero.welcome, en.hero.welcome);
  add("hero.title", "Überschrift", "Startseite", de.hero.title, en.hero.title);
  add("hero.subtitle", "Einleitung", "Startseite", de.hero.subtitle, en.hero.subtitle, true);
  add("hero.cta", "Schaltfläche Buchen", "Startseite", de.hero.cta, en.hero.cta);
  add("hero.explore", "Schaltfläche Anfahrt", "Startseite", de.hero.explore, en.hero.explore);

  add("nav.campsite", "Menü: Campingplatz", "Navigation", de.nav.campsite, en.nav.campsite);
  add("nav.sitePlan", "Menü: Platzplan", "Navigation", "Platzplan", "Site map");
  add("nav.gallery", "Menü: Galerie", "Navigation", de.nav.gallery, en.nav.gallery);
  add("nav.prices", "Menü: Preise", "Navigation", de.nav.prices, en.nav.prices);
  add("nav.directions", "Menü: Anfahrt", "Navigation", de.nav.directions, en.nav.directions);
  add("nav.booking", "Menü: Buchung", "Navigation", de.nav.booking, en.nav.booking);

  add("experience.title", "Überschrift", "Der Platz", de.experience.title, en.experience.title);
  add("experience.subtitle", "Einleitung", "Der Platz", de.experience.subtitle, en.experience.subtitle, true);
  for (const item of de.experience.items) {
    const english = en.experience.items.find((entry) => entry.key === item.key);
    add(`experience.items.${item.key}.title`, `${item.title}: Überschrift`, "Der Platz", item.title, english?.title ?? "");
    add(
      `experience.items.${item.key}.description`,
      `${item.title}: Text`,
      "Der Platz",
      item.description,
      english?.description ?? "",
      true,
    );
  }

  add("sitePlan.title", "Überschrift", "Platzplan", de.sitePlan.title, en.sitePlan.title);
  add("sitePlan.subtitle", "Einleitung", "Platzplan", de.sitePlan.subtitle, en.sitePlan.subtitle, true);
  add("sitePlan.hint", "Hinweis am Plan", "Platzplan", de.sitePlan.hint, en.sitePlan.hint);
  add("sitePlan.legendTitle", "Legende", "Platzplan", de.sitePlan.legendTitle, en.sitePlan.legendTitle);

  add("facilities.title", "Überschrift", "Ausstattung", de.facilities.title, en.facilities.title);
  add("facilities.subtitle", "Einleitung", "Ausstattung", de.facilities.subtitle, en.facilities.subtitle, true);
  add("facilities.learnMore", "Linktext", "Ausstattung", de.facilities.learnMore, en.facilities.learnMore);
  add("facilities.sitePlanLabel", "Link zum Platzplan", "Ausstattung", de.facilities.sitePlanLabel, en.facilities.sitePlanLabel);
  for (const tab of de.facilities.tabs) {
    addFacilityTab(add, tab, en.facilities.tabs.find((entry) => entry.id === tab.id));
  }

  add("gallery.title", "Überschrift", "Fotos", de.gallery.title, en.gallery.title);
  add("gallery.subtitle", "Einleitung", "Fotos", de.gallery.subtitle, en.gallery.subtitle, true);
  add("gallery.ctaText", "Text über der Buchung", "Fotos", de.gallery.ctaText, en.gallery.ctaText);
  add("gallery.cta", "Schaltfläche", "Fotos", de.gallery.cta, en.gallery.cta);

  add("surroundings.title", "Überschrift", "Umgebung", de.surroundings.title, en.surroundings.title);
  add("surroundings.subtitle", "Einleitung", "Umgebung", de.surroundings.subtitle, en.surroundings.subtitle, true);
  for (const item of de.surroundings.items) {
    const english = en.surroundings.items.find((entry) => entry.key === item.key);
    add(`surroundings.items.${item.key}.name`, `${item.name}: Name`, "Umgebung", item.name, english?.name ?? "");
    add(`surroundings.items.${item.key}.distance`, `${item.name}: Entfernung`, "Umgebung", item.distance, english?.distance ?? "");
    add(
      `surroundings.items.${item.key}.description`,
      `${item.name}: Text`,
      "Umgebung",
      item.description,
      english?.description ?? "",
      true,
    );
  }

  add("openingHours.title", "Überschrift", "Besuch", de.openingHours.title, en.openingHours.title);
  add("openingHours.teaser", "Einleitung", "Besuch", de.openingHours.teaser, en.openingHours.teaser, true);
  const paragraphCount = Math.max(de.openingHours.paragraphs.length, en.openingHours.paragraphs.length);
  for (let index = 0; index < paragraphCount; index += 1) {
    add(
      `openingHours.paragraphs.${index}`,
      `Absatz ${index + 1}`,
      "Besuch",
      de.openingHours.paragraphs[index] ?? "",
      en.openingHours.paragraphs[index] ?? "",
      true,
    );
  }
  add("openingHours.signature", "Signatur", "Besuch", de.openingHours.signature, en.openingHours.signature);
  add("contact.title", "Kontakt-Überschrift", "Besuch", de.contact.title, en.contact.title);
  add("contact.teaser", "Kontakttext", "Besuch", de.contact.teaser, en.contact.teaser, true);
  add("contact.reachUs", "Erreichbarkeit", "Besuch", de.contact.reachUs, en.contact.reachUs, true);

  add("directions.title", "Überschrift", "Anfahrt", de.directions.title, en.directions.title);
  de.directions.routes.forEach((route, routeIndex) => {
    const english = en.directions.routes[routeIndex];
    const stepCount = Math.max(route.steps.length, english?.steps.length ?? 0);
    for (let stepIndex = 0; stepIndex < stepCount; stepIndex += 1) {
      add(
        `directions.routes.${routeIndex}.steps.${stepIndex}`,
        `${route.via}, Schritt ${stepIndex + 1}`,
        "Anfahrt",
        route.steps[stepIndex] ?? "",
        english?.steps[stepIndex] ?? "",
        true,
      );
    }
  });

  add("prices.title", "Seitentitel", "Preise", de.pricesPage.title, en.pricesPage.title);
  add("prices.subtitle", "Untertitel", "Preise", de.pricesPage.subtitle, en.pricesPage.subtitle);
  add("prices.pitchesHeading", "Überschrift Stellplätze", "Preise", de.pricesPage.pitchesHeading, en.pricesPage.pitchesHeading);
  add("prices.pitchesHint", "Hinweis", "Preise", de.pricesPage.pitchesHint, en.pricesPage.pitchesHint, true);
  add("prices.additionalHeading", "Überschrift weitere Preise", "Preise", de.pricesPage.additionalHeading, en.pricesPage.additionalHeading);
  add("prices.disclaimer", "Hinweis unter der Rechnung", "Preise", de.pricesPage.disclaimer, en.pricesPage.disclaimer, true);
  add("prices.cta", "Schaltfläche", "Preise", de.pricesPage.cta, en.pricesPage.cta);
  for (const pitch of pitchPrices) {
    const english = pitchPricesEn.find((entry) => entry.id === pitch.id);
    add(`prices.pitches.${pitch.id}.name`, `${pitch.name}: Name`, "Preise", pitch.name, english?.name ?? "");
    add(`prices.pitches.${pitch.id}.note`, `${pitch.name}: Hinweis`, "Preise", pitch.note, english?.note ?? "");
  }
  for (const item of additionalPrices.filter((entry) => !entry.perPerson)) {
    const english = additionalPricesEn.find((entry) => entry.id === item.id);
    add(`prices.additional.${item.id}.name`, `${item.name}: Name`, "Preise", item.name, english?.name ?? "");
    add(`prices.additional.${item.id}.note`, `${item.name}: Hinweis`, "Preise", item.note, english?.note ?? "");
  }

  return fields;
}

function addFacilityTab(
  add: (key: string, label: string, group: string, german: string, english: string, multiline?: boolean) => void,
  tab: FacilityTab,
  english: FacilityTab | undefined,
) {
  const group = `Ausstattung: ${tab.label}`;
  add(`facilities.tabs.${tab.id}.label`, "Karte: Bezeichnung", group, tab.label, english?.label ?? "");
  add(`facilities.tabs.${tab.id}.heading`, "Überschrift", group, tab.heading, english?.heading ?? "");
  add(`facilities.tabs.${tab.id}.summary`, "Kurztext", group, tab.summary, english?.summary ?? "", true);
  tab.highlights.forEach((highlight, index) => {
    add(
      `facilities.tabs.${tab.id}.highlights.${index}`,
      `Stichpunkt ${index + 1}`,
      group,
      highlight,
      english?.highlights[index] ?? "",
    );
  });
  tab.sections.forEach((section, sectionIndex) => {
    const enSection = english?.sections[sectionIndex];
    add(
      `facilities.tabs.${tab.id}.sections.${sectionIndex}.title`,
      `${section.title}: Überschrift`,
      group,
      section.title,
      enSection?.title ?? "",
    );
    add(
      `facilities.tabs.${tab.id}.sections.${sectionIndex}.text`,
      `${section.title}: Text`,
      group,
      section.text,
      enSection?.text ?? "",
      true,
    );
    section.list?.included.forEach((item, itemIndex) => {
      add(
        `facilities.tabs.${tab.id}.sections.${sectionIndex}.included.${itemIndex}`,
        `Enthalten ${itemIndex + 1}`,
        group,
        item,
        enSection?.list?.included[itemIndex] ?? "",
        true,
      );
    });
    section.list?.excluded.forEach((item, itemIndex) => {
      add(
        `facilities.tabs.${tab.id}.sections.${sectionIndex}.excluded.${itemIndex}`,
        `Nicht enthalten ${itemIndex + 1}`,
        group,
        item,
        enSection?.list?.excluded[itemIndex] ?? "",
        true,
      );
    });
  });
}

function imageFields(): WebsiteImageField[] {
  const fields: WebsiteImageField[] = [];
  const add = (key: string, label: string, group: string, src: string, altDe: string, altEn = altDe) => {
    fields.push({ key, label, group, src, altDe, altEn });
  };
  add("logo", "Logo", "Bilder: Kopfzeile", "/images/signaturlogo.jpg", "Campingplatz Mainkur");
  add("hero", "Titelbild", "Bilder: Startseite", siteImages.hero.src, siteImages.hero.alt);
  for (const item of de.experience.items) {
    const image = siteImages.experience[item.key as keyof typeof siteImages.experience];
    add(`experience.${item.key}`, item.title, "Bilder: Der Platz", image.src, image.alt);
  }
  for (const tab of de.facilities.tabs) {
    add(`facilities.${tab.id}`, tab.label, "Bilder: Ausstattung", tab.image, tab.heading);
  }
  galleryImages.forEach((image, index) => {
    add(`gallery.${index}`, `Foto ${index + 1}`, "Bilder: Galerie", image.src, image.alt);
  });
  for (const item of de.surroundings.items) {
    const image = siteImages.surroundings[item.key as keyof typeof siteImages.surroundings];
    add(`surroundings.${item.key}`, item.name, "Bilder: Umgebung", image.src, image.alt);
  }
  add("contact", "Kontaktfoto", "Bilder: Besuch", siteImages.contact.src, siteImages.contact.alt);
  add("directions", "Anfahrt", "Bilder: Anfahrt", siteImages.directions.src, siteImages.directions.alt);
  for (const pitch of pitchPrices) {
    add(`prices.pitches.${pitch.id}`, pitch.name, "Bilder: Preise", pitch.image.src, pitch.image.alt);
  }
  return fields;
}

export const WEBSITE_TEXT_FIELDS = textFields();
export const WEBSITE_IMAGE_FIELDS = imageFields();

export const WEBSITE_SHARED_FIELDS: WebsiteSharedField[] = [
  { key: "site.host", label: "Gastgeber", group: "Kontaktdaten", value: "Familie Heike Borgstedt" },
  { key: "site.address", label: "Adresse", group: "Kontaktdaten", value: site.address },
  { key: "site.phone", label: "Telefon", group: "Kontaktdaten", value: site.phone },
  { key: "site.email", label: "E-Mail", group: "Kontaktdaten", value: site.email },
  { key: "site.reception.morning", label: "Rezeption vormittags", group: "Kontaktdaten", value: site.reception.morning },
  { key: "site.reception.afternoon", label: "Rezeption nachmittags", group: "Kontaktdaten", value: site.reception.afternoon },
  { key: "site.season.open", label: "Saisonbeginn", group: "Kontaktdaten", value: site.season.open },
  { key: "site.season.close", label: "Saisonende", group: "Kontaktdaten", value: site.season.close },
  { key: "site.closed.from", label: "Geschlossen ab", group: "Kontaktdaten", value: site.closed.from },
  { key: "site.closed.until", label: "Geschlossen bis", group: "Kontaktdaten", value: site.closed.until },
  { key: "site.pitchAvailable", label: "Platz frei ab", group: "Kontaktdaten", value: site.pitchAvailable },
  { key: "site.departure", label: "Abreise bis", group: "Kontaktdaten", value: site.departure },
  { key: "site.nightQuiet", label: "Nachtruhe", group: "Kontaktdaten", value: site.nightQuiet },
  { key: "site.gps", label: "GPS-Angabe", group: "Kontaktdaten", value: site.gps },
  { key: "site.lat", label: "Karten-Breite", group: "Kontaktdaten", value: String(site.gpsCoords.lat) },
  { key: "site.lng", label: "Karten-Länge", group: "Kontaktdaten", value: String(site.gpsCoords.lng) },
];

export function publicWebsiteOrigin(hostname: string): string {
  if (hostname.startsWith("dev.app.")) return `https://dev.www.${hostname.slice("dev.app.".length)}`;
  if (hostname.startsWith("test.app.")) return `https://test.www.${hostname.slice("test.app.".length)}`;
  if (hostname.startsWith("app.")) return `https://www.${hostname.slice("app.".length)}`;
  return "http://localhost:4321";
}

export function resolveWebsiteAsset(src: string, hostname: string): string {
  if (src.startsWith("/api/")) return src;
  if (src.startsWith("/images/")) return `${publicWebsiteOrigin(hostname)}${src}`;
  return src;
}
